import { Mutex } from 'async-mutex';
import { AxiosResponse } from 'axios';
import { SSO_REFRESH_BASIC_AUTH } from 'config';
import { authTokenKey } from 'services/session/session.config';
import {
  AuthType,
  cleanUpAndEndUserSession,
  getAuthType,
} from 'services/session/session.helpers';
import { selectAuth } from 'services/session/session.selectors';
import { withCurrentStore } from 'store/currentStore';
import { createScopedErrorHandler } from 'utils/functions/handleError';
import apiRequest from './api.config';
import { logValidationErrors } from './api.helpers';
import { makeSelectAuthApiConfig } from './api.selectors';
import { BaseRequestApiDataPayload } from './api.types';

const mutex = new Mutex();

const handleApiSagaError = createScopedErrorHandler('saga');

export enum BaseFetcherResult {
  SUCCESS = 'SUCCESS',
  API_REQUEST_FAILED = 'API_REQUEST_FAILED',
}

export type BaseFetcherReturn = {
  payload: BaseRequestApiDataPayload;
  response?: AxiosResponse<any>;
  error?: unknown;
  result: BaseFetcherResult;
};

export const baseFetcher = async (
  payload: BaseRequestApiDataPayload,
): Promise<BaseFetcherReturn> => {
  // Used for the refresh token mechanism
  await mutex.waitForUnlock();

  const ssoRefreshPathname = '/authentication-ms/v1/sso/refresh';

  const currentRequestIsRefreshToken =
    payload.requestConfig.url.includes(ssoRefreshPathname);

  try {
    const response = await apiRequest.request(payload.requestConfig);

    if (payload.successSchemaValidator) {
      logValidationErrors(
        payload.successSchemaValidator,
        payload.requestConfig.method,
        payload.requestConfig.url,
        response,
      );
    }
    const authType = getAuthType();
    const isSso = authType === AuthType.Sso;

    // If X-JWT-Expiring header is present, block everything, refresh the JWT token,
    // then release the lock
    if (
      !currentRequestIsRefreshToken &&
      response.headers['x-jwt-expiring'] &&
      isSso
    ) {
      if (!mutex.isLocked()) {
        const release = await mutex.acquire();

        const state = withCurrentStore((store) => store.getState());
        const auth = selectAuth(state);

        const refreshTokenRequestConfig = makeSelectAuthApiConfig({
          url: ssoRefreshPathname,
          headers: { authorization: `Basic ${SSO_REFRESH_BASIC_AUTH}` },
          method: 'POST',
          enrichRequestData: false,
          data: { jwt: auth.authToken },
        })(state);

        try {
          const refreshTokenResponse = await apiRequest.request(
            refreshTokenRequestConfig,
          );

          const refreshedJwt = refreshTokenResponse.data.jwt;

          if (isSso) {
            window.postMessage(
              { operation: 'refresh', value: { JWT: refreshedJwt } },
              '*',
            );
          }

          window.localStorage.setItem(authTokenKey, refreshedJwt);

          release();
        } catch (refreshTokenRequestError) {
          handleApiSagaError(refreshTokenRequestError);
          release();

          cleanUpAndEndUserSession();

          return {
            result: BaseFetcherResult.API_REQUEST_FAILED,
            payload,
            error: refreshTokenRequestError,
          };
        }
      }
    }

    return {
      result: BaseFetcherResult.SUCCESS,
      payload,
      response,
    };
  } catch (sagaError) {
    handleApiSagaError(sagaError);

    return {
      result: BaseFetcherResult.API_REQUEST_FAILED,
      payload,
      error: sagaError,
    };
  }
};
